import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { ConnectedPosition } from '@angular/cdk/overlay';
import { EMD_DATE_MAX, EMD_DATE_MIN, EmdChip } from '../emd-mock-data';
import { EmdDateRange } from '../emd-filter.service';

/** ISO 'YYYY-MM-DD' → local-midnight Date (avoids the UTC drift of new Date(iso)). */
function isoToDate(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
}

/** Local Date → ISO 'YYYY-MM-DD' (no UTC drift). */
function dateToIso(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * "Selected filters:" row — the single source of truth for every applied filter.
 *
 * Mirrors the app's filter badges widget (widget/filter-badges): each dimension
 * chip opens a CDK menu with the same include / exclude / clear header, search
 * box, select-all row and checkbox option list.
 *
 * Cross-filter clicks on the visuals are staged as a pending selection and, on
 * confirm, written straight into these badge dropdowns by the shell — so the
 * applied filter lives on the main badge, not as a separate chip.
 */
@Component({
    selector: 'emd-filter-bar',
    templateUrl: './emd-filter-bar.component.html',
    styleUrls: ['./emd-filter-bar.component.scss'],
    standalone: false
})
export class EmdFilterBarComponent {
  @Input() chips: EmdChip[] = [];
  @Input() showFilterButton = false;
  /** Applied Time Range bounds for the active tab. */
  @Input() dateRange: EmdDateRange = {};
  /** Client brand colour; drives the chip/date accents via a --emd-accent CSS var. */
  @Input() accentColor?: string;

  @Output() reset = new EventEmitter<void>();
  @Output() filterClick = new EventEmitter<void>();
  /** Emits the chip whose applied selection changed. */
  @Output() chipChange = new EventEmitter<EmdChip>();
  /** Emits the new Time Range bounds ({} clears the cut). */
  @Output() dateRangeChange = new EventEmitter<EmdDateRange>();

  activeChip: EmdChip | null = null;
  searchTerm = '';
  /** Working selection for the open menu. */
  draft: string[] = [];

  // ── Chip strip scrolling ────────────────────────────────────────────────────
  // As in widget/filter-badges: arrows appear when the strip overflows and
  // slide it 150px per click.

  @ViewChild('chipsScroll') private chipsScroll?: ElementRef<HTMLElement>;

  get showScrollArrows(): boolean {
    const el = this.chipsScroll?.nativeElement;
    return !!el && el.scrollWidth > el.clientWidth;
  }

  scrollChips(direction: 1 | -1): void {
    const el = this.chipsScroll?.nativeElement;
    el?.scrollTo({ left: el.scrollLeft + direction * 150, behavior: 'smooth' });
  }

  // ── Time Range badge ────────────────────────────────────────────────────────
  // Reuses the app's dual-month calendar (widget/filter-badges/dual-calendar,
  // <app-dual-calendar>) — the very component filter-badges' #datemenu renders
  // for a D/W date range (timeCycleSelection). It is exported by WidgetModule,
  // which DashboardModule imports, so EMD consumes it directly (no clone, no
  // shared file touched). Driven by an EMD-built DateConfig/selectedData and
  // mapped back to EmdDateRange on apply; hosted in a self-managed
  // cdkConnectedOverlay (transparent backdrop) triggered by the chip.

  /** Bounds of the extract (ISO + Date). */
  readonly dateMin = EMD_DATE_MIN;
  readonly dateMax = EMD_DATE_MAX;
  readonly calMin = isoToDate(EMD_DATE_MIN);
  readonly calMax = isoToDate(EMD_DATE_MAX);

  /** Static config for <app-dual-calendar>: 'D' = day-grained range, no weekday
   *  restriction, capped to the extract (shape mirrors filter-badges' DateConfig). */
  readonly dateConfig = {
    selectedType: 'D',
    option: {
      minDate: this.calMin,
      maxDate: this.calMax,
      isRestriction: 'no',
      compulsaryFilter: true,
    },
  };

  /** Seeded from the applied range each time the panel opens — a fresh object
   *  ref so the calendar's ngOnChanges re-runs and repaints the selection. */
  dateSelectedData: { from_date: Date; to_date: Date } = {
    from_date: this.calMin,
    to_date: this.calMax,
  };

  /** Panel open state + placement for the connected overlay. The chip is the
   *  last one on the right and the calendar is wide, so prefer right-aligned
   *  (opens leftward) to stay on-screen; left-aligned are fallbacks, and
   *  cdkConnectedOverlayPush nudges it fully into view if neither fits. */
  dateRangeOpen = false;
  readonly dateRangePositions: ConnectedPosition[] = [
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 6 },
    { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -6 },
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 6 },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -6 },
  ];

  /** Chip click: seed the calendar from the applied range (or the full extract
   *  when none is set), then toggle the panel. */
  toggleDateRange(): void {
    if (!this.dateRangeOpen) {
      const from = this.dateRange.from ?? this.dateMin;
      const to = this.dateRange.to ?? this.dateMax;
      this.dateSelectedData = { from_date: isoToDate(from), to_date: isoToDate(to) };
    }
    this.dateRangeOpen = !this.dateRangeOpen;
  }

  closeDateRange(): void {
    this.dateRangeOpen = false;
  }

  /**
   * dual-calendar's Apply hands back {from_date, to_date} as Dates. Map to the
   * inclusive ISO cut; a reversed pair is swapped rather than rejected.
   */
  onDualCalendarApply(range: { from_date: Date; to_date: Date }): void {
    if (!range?.from_date || !range?.to_date) { return; }
    let from = dateToIso(new Date(range.from_date));
    let to = dateToIso(new Date(range.to_date));
    if (from > to) { [from, to] = [to, from]; }
    this.dateRangeChange.emit({ from, to });
    this.closeDateRange();
  }

  get hasDateRange(): boolean {
    return !!(this.dateRange.from || this.dateRange.to);
  }

  /**
   * Chip caption: always the effective window as "MM/DD/YY - MM/DD/YY" (like
   * the app's date chip), falling back to the extract bounds when no explicit
   * cut is applied so the chip shows real dates rather than a placeholder.
   */
  get dateRangeLabel(): string {
    const fmt = (iso: string) => {
      const [y, m, d] = iso.split('-');
      return `${m}/${d}/${y.slice(2)}`;
    };
    const from = this.dateRange.from ?? this.dateMin;
    const to = this.dateRange.to ?? this.dateMax;
    return `${fmt(from)} - ${fmt(to)}`;
  }

  openChip(chip: EmdChip): void {
    this.activeChip = chip;
    this.draft = [...chip.selected];
    this.searchTerm = '';
  }

  get visibleOptions(): string[] {
    const options = this.activeChip?.options ?? [];
    const term = this.searchTerm.trim().toLowerCase();
    return term
      ? options.filter((o) => o.toLowerCase().includes(term))
      : options;
  }

  isSelected(option: string): boolean {
    return this.draft.includes(option);
  }

  toggle(option: string): void {
    if (!this.activeChip) { return; }

    if (!this.activeChip.multi) {
      // Single-select behaves like a radio and applies straight away.
      this.draft = [option];
      this.commit();
      return;
    }

    this.draft = this.isSelected(option)
      ? this.draft.filter((o) => o !== option)
      : [...this.draft, option];
  }

  /** True when every currently visible option is selected. */
  areAllSelected(): boolean {
    const visible = this.visibleOptions;
    return visible.length > 0 && visible.every((o) => this.isSelected(o));
  }

  toggleSelectAll(): void {
    const visible = this.visibleOptions;
    if (this.areAllSelected()) {
      this.draft = this.draft.filter((o) => !visible.includes(o));
    } else {
      this.draft = [...new Set([...this.draft, ...visible])];
    }
  }

  /**
   * Clear this dimension's filter, matching filter-badges: select every option
   * (which `chipsToFilter` treats as no cut) and apply at once, so the count
   * returns to N/N and the data unfilters. The old version only emptied the
   * in-menu draft without committing, and Include was disabled on an empty
   * draft, so the action dead-ended and nothing changed.
   */
  clearSelection(): void {
    if (!this.activeChip) { return; }
    this.draft = [...this.activeChip.options];
    this.commit();
  }

  /** Include keeps the draft; exclude keeps everything else. */
  apply(mode: 'include' | 'exclude'): void {
    if (!this.activeChip) { return; }
    if (mode === 'exclude') {
      this.draft = this.activeChip.options.filter((o) => !this.draft.includes(o));
    }
    this.commit();
  }

  private commit(): void {
    if (!this.activeChip) { return; }
    this.activeChip.selected = [...this.draft];
    this.chipChange.emit(this.activeChip);
  }
}
